import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CubeTransparentIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import React from 'react';
import { FileCard } from './FileCard';
import { FileRow } from './FileRow';
import type { Asset, FileCategory, LayoutMode } from './types';

interface FileCategorySectionProps {
    category: FileCategory;
    isExpanded: boolean;
    isDownloading: boolean;
    layoutMode?: LayoutMode;
    thumbnailUrls?: Record<string, string>;
    onToggle: () => void;
    onPreviewFile: (asset: Asset) => void;
    onPreviewCategory: () => void;
    /** Opens the full STL viewer - supplied only for the 3D Models section. */
    onOpenStlViewer?: () => void;
    onDownloadCategory: () => void;
    onDownloadFile: (asset: Asset) => Promise<void>;
    onDeleteFile?: (asset: Asset) => void;
    onRenameFile?: (asset: Asset) => void;
    selectedAssets?: Set<string>;
    onToggleSelect?: (assetId: string) => void;
    selectionMode?: boolean;
    onThumbnailError?: (assetId: string) => void;
    thumbUrlAssetIds?: ReadonlySet<string>;
    /** AssetIds with an active CBCT processing job - shows a card badge. */
    processingAssetIds?: ReadonlySet<string>;
}

const FileCategorySection: React.FC<FileCategorySectionProps> = ({
    category,
    isExpanded,
    isDownloading,
    layoutMode = 'list',
    thumbnailUrls,
    onToggle,
    onPreviewFile,
    onPreviewCategory,
    onOpenStlViewer,
    onDownloadCategory,
    onDownloadFile,
    onDeleteFile,
    onRenameFile,
    selectedAssets,
    onToggleSelect,
    selectionMode,
    onThumbnailError,
    thumbUrlAssetIds,
    processingAssetIds
}) => (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <div className="flex items-center justify-between gap-3">
            <button
                onClick={onToggle}
                className="flex items-center gap-2 flex-1 text-left hover:opacity-80 transition-opacity min-w-0"
            >
                {isExpanded ? (
                    <ChevronUpIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                ) : (
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                )}
                <div className="p-1.5 bg-white rounded-md border border-gray-200 text-gray-500 flex-shrink-0">
                    {category.icon}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-gray-800">{category.label}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                        {category.assets.length} {category.assets.length === 1 ? 'file' : 'files'}
                    </p>
                </div>
            </button>
            <div className="flex-shrink-0 flex items-center gap-2">
                {category.previewable && category.assets.length > 0 && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onPreviewCategory();
                        }}
                        className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium"
                    >
                        <EyeIcon className="w-3.5 h-3.5" />
                        Preview
                    </button>
                )}
                {onOpenStlViewer && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenStlViewer();
                        }}
                        className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium"
                    >
                        <CubeTransparentIcon className="w-3.5 h-3.5" />
                        3D Viewer
                    </button>
                )}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDownloadCategory();
                    }}
                    disabled={isDownloading || category.assets.length === 0}
                    className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 px-2.5 py-1.5 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isDownloading ? (
                        <>
                            <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" />
                            Downloading...
                        </>
                    ) : (
                        <>
                            <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                            Download
                        </>
                    )}
                </button>
            </div>
        </div>
        {isExpanded &&
            (layoutMode === 'grid' ? (
                <div className="mt-3 grid grid-cols-[repeat(auto-fill,minmax(110px,1fr))] gap-3">
                    {category.assets.map((asset) => {
                        const key = String(asset.assetId);
                        return (
                            <FileCard
                                key={asset.assetId}
                                asset={asset}
                                thumbnailUrl={thumbnailUrls?.[key]}
                                onPreviewFile={onPreviewFile}
                                isSelected={selectedAssets?.has(key)}
                                onToggleSelect={onToggleSelect}
                                selectionMode={selectionMode}
                                onThumbnailError={
                                    thumbUrlAssetIds?.has(key) ? onThumbnailError : undefined
                                }
                                isProcessing={processingAssetIds?.has(key)}
                            />
                        );
                    })}
                </div>
            ) : (
                <div className="mt-3 space-y-2">
                    {category.assets.map((asset) => (
                        <FileRow
                            key={asset.assetId}
                            asset={asset}
                            onPreviewFile={onPreviewFile}
                            onDownloadFile={onDownloadFile}
                            onDeleteFile={onDeleteFile}
                            onRenameFile={onRenameFile}
                            isSelected={selectedAssets?.has(String(asset.assetId))}
                            onToggleSelect={onToggleSelect}
                            selectionMode={selectionMode}
                        />
                    ))}
                </div>
            ))}
    </div>
);

export { FileCategorySection };
